'use client';

import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useRef, useState, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '@/lib/auth-context';
import { useT } from '@/lib/i18n';
import { api } from '@/lib/api';
import { Library } from '@/types';

const PRIMARY_ITEM_SPECS: { key: string; icon: string; href: string; fallback?: string }[] = [
  { key: 'nav.home',        icon: 'home',                  href: '/' },
  { key: 'nav.explore',     icon: 'explore',               href: '/explore' },
  { key: 'nav.trending',    icon: 'local_fire_department', href: '/trending' },
  { key: 'nav.collections', icon: 'library_books',         href: '/collections' },
  { key: 'nav.all_tags',    icon: 'sell',                  href: '/tags' },
  { key: 'nav.models',      icon: 'smart_toy',             href: '/models', fallback: 'AI models' },
];

const FALLBACK_LIBRARIES: Pick<Library, 'slug' | 'name' | 'icon'>[] = [
  { slug: 'writing',   name: 'Writing',   icon: 'edit_note' },
  { slug: 'coding',    name: 'Coding',    icon: 'code' },
  { slug: 'art',       name: 'Art',       icon: 'palette' },
  { slug: 'business',  name: 'Business',  icon: 'business_center' },
  { slug: 'education', name: 'Education', icon: 'school' },
];

function NavLink({
  href,
  icon,
  label,
  active,
  count,
  onClick,
}: {
  href: string;
  icon: string;
  label: string;
  active: boolean;
  count?: number;
  onClick?: () => void;
}) {
  return (
    <Link
      href={href}
      className="nav-item no-underline"
      aria-current={active ? 'true' : undefined}
      onClick={onClick}
    >
      <span className="material-symbols-outlined nav-icon">{icon}</span>
      <span>{label}</span>
      {count ? <span className="count">{count}</span> : null}
    </Link>
  );
}

function DrawerNav({ onClose }: { onClose: () => void }) {
  const pathname = usePathname();
  const router = useRouter();
  const { user } = useAuth();
  const t = useT();

  const [libraries, setLibraries] =
    useState<Pick<Library, 'slug' | 'name' | 'icon'>[]>(FALLBACK_LIBRARIES);

  useEffect(() => {
    api.libraries
      .list()
      .then((rows) => {
        if (rows.length > 0)
          setLibraries(rows.map((r) => ({ slug: r.slug, name: r.name, icon: r.icon ?? 'folder' })));
      })
      .catch(() => {});
  }, []);

  const primaryItems = PRIMARY_ITEM_SPECS.map((s) => {
    const translated = t(s.key);
    const label = (translated === s.key && s.fallback) ? s.fallback : translated;
    return { label, icon: s.icon, href: s.href };
  });
  const savedItems = [
    { label: t('nav.saved'),      icon: 'bookmark',  href: '/my/saved' },
    { label: t('nav.my_prompts'), icon: 'grid_view', href: '/my/prompts' },
  ];

  const close = () => onClose();

  return (
    <>
      {/* Logo */}
      <Link href="/" className="sidebar-logo no-underline" onClick={close}>
        <span className="mark">P</span>
        <span>Prompt Save</span>
      </Link>

      {/* New prompt CTA */}
      <Link
        href={user ? '/create' : '/auth/login'}
        className="ps-btn ps-btn-accent no-underline"
        style={{ width: '100%', justifyContent: 'center', marginBottom: 10 }}
        onClick={close}
      >
        <span className="material-symbols-outlined ps-icon">add</span>
        {t('nav.new_prompt')}
      </Link>

      {primaryItems.map((it) => (
        <NavLink
          key={it.href}
          {...it}
          active={pathname === it.href || (it.href === '/' && pathname === '/')}
          onClick={close}
        />
      ))}

      {user && (
        <NavLink
          label={t('nav.following')}
          icon="group"
          href="/following"
          active={pathname === '/following'}
          onClick={close}
        />
      )}

      {user &&
        savedItems.map((it) => (
          <NavLink key={it.href} {...it} active={pathname === it.href} onClick={close} />
        ))}

      {/* Admin link removed — admin console is a separate app at /admin/login. */}

      <div className="sidebar-section">{t('nav.libraries')}</div>
      {libraries.map((lib) => {
        const href = `/library/${lib.slug}`;
        return (
          <NavLink
            key={lib.slug}
            label={lib.name}
            icon={lib.icon ?? 'folder'}
            href={href}
            active={pathname === href}
            onClick={() => { router.push(href); close(); }}
          />
        );
      })}

      <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {user ? (
          <NavLink
            label={t('nav.profile')}
            icon="person"
            href={`/profile/${user.username}`}
            active={pathname === `/profile/${user.username}`}
            onClick={close}
          />
        ) : (
          <NavLink
            label={t('nav.sign_in')}
            icon="login"
            href="/auth/login"
            active={pathname.startsWith('/auth')}
            onClick={close}
          />
        )}
      </div>
    </>
  );
}

function MobileDrawerInner({ triggerRef }: { triggerRef: React.RefObject<HTMLButtonElement | null> }) {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const prevPathRef = useRef(pathname);

  useEffect(() => { setMounted(true); }, []);

  // Listen for open event from hamburger button
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('mobiledrawer:open', handler);
    return () => window.removeEventListener('mobiledrawer:open', handler);
  }, []);

  // Close on route change
  useEffect(() => {
    if (prevPathRef.current !== pathname) {
      prevPathRef.current = pathname;
      setOpen(false);
    }
  }, [pathname]);

  // ESC key to close + close when resizing past 820px
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    const onResize = () => {
      if (window.innerWidth > 820) close();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Body scroll lock
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  // Focus trap: move focus into drawer when opened, restore on close
  useEffect(() => {
    if (open && drawerRef.current) {
      const focusable = drawerRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      focusable[0]?.focus();
    } else if (!open && triggerRef.current) {
      triggerRef.current.focus();
    }
  }, [open, triggerRef]);

  const close = () => {
    setOpen(false);
    window.dispatchEvent(new CustomEvent('mobiledrawer:close'));
  };

  if (!mounted) return null;

  const drawer = (
    <>
      <div
        className={`ps-drawer-backdrop${open ? ' open' : ''}`}
        aria-hidden="true"
        onClick={close}
      />
      <div
        ref={drawerRef}
        className={`ps-drawer${open ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <Suspense fallback={null}>
          <DrawerNav onClose={close} />
        </Suspense>
      </div>
    </>
  );

  return createPortal(drawer, document.body);
}

export default function MobileDrawer({ triggerRef }: { triggerRef: React.RefObject<HTMLButtonElement | null> }) {
  return (
    <Suspense fallback={null}>
      <MobileDrawerInner triggerRef={triggerRef} />
    </Suspense>
  );
}
